// THE home widget FRAME (HOME_WIDGETS_SPEC.md §2, §3, §6) — one definition
// for BOTH widget planes: the page's own widgets (pages/main.js `_widget`)
// and module / app / AI widgets (`HomeModuleWidget`). The two planes used to
// carry two hand-mirrored copies of this chrome ("change both together");
// now each only supplies its state row and its Body.
//
// The element IS the grid item and the card (no wrapper): the board's CSS
// grid places it by `data-widget-size` and CSS `order`, and it paints the one
// `card` surface — `var(--home-card-bg, var(--theme-card-background))`, the
// brand card fill behind the existing re-tint channel (a workspace record or a
// module's chrome.card). No literal fill, ring or blur here: the ambient skin
// re-values the card token (HOME_AMBIENT_SKIN_SPEC).
//
// Its STATE ROW is the widget descriptor (functions/homeWidgetFrame.js):
//   { id, title, icon, kind: 'widget', size, minSize, hue,
//     openRoute, openCall?, action?, help?, moduleId?, spec? }
//
// Anatomy — header: the hue plate, the title (a real button: the widget's
// keyboard entry), the optional (?) and ONE square button (1:1, the widget's
// primary verb or ↗ Open); then the body. App shortcuts are never frames:
// they live in the App strip above the grid (components/HomeAppStrip.js).
//
// Quality + overflow laws (HOME_AMBIENT_SKIN_SPEC §3, §3b): the card never
// clips — no overflow hidden; the title wraps (overflow-wrap anywhere),
// never an ellipsis; hover is an inset wash on the card, nothing scales.
//
// Edit mode (`root._homeCustomizeMode`): the card is draggable — a drag moves
// the widget in the ORDER and the grid reflows (functions/homeWidgetDrag.js);
// a remove button and a size picker (S · M · L · XL, never below the widget's
// minimum) overlay the card, so entering edit mode moves nothing. A drag
// starts anywhere but a control; `.gs-no-drag` (the class bodies already
// carry, named in the GridStack era) marks an area that is never a handle.
//
// Resize (DESIGN-WL-HOME-WIDTH-RESIZE-1): the corner handle
// (`HomeWidgetResizeHandle`, components/HomeWidgetResizeHandle.js, bottom-right — shown on hover and focus-within, and
// always in edit mode) drags a dashed outline (`WidgetResizeGhost`) that snaps
// to the four sizes, never below the minimum; the release writes the size
// through the same durable layout write as the picker
// (functions/homeWidgetResize.js). Both are overlays: showing them moves
// nothing.
//
// Motion (functions/homeWidgetMotion.js): the card declares the transitions a
// move plays — `translate` (a reflow glide) and `clip-path` (a grow reveal) at
// `C`, `scale` (the drag lift) at `A`, all `defaultBezier`. Code only ever
// writes the START value inline and clears it; the brand reset lands every one
// at once under reduced motion.

export const HomeWidgetFrame = {
  class: 'home-grid-item',
  attr: {
    'data-widget-id': (el, s) => (s && s.id) || null,
    'data-widget-size': (el, s) => el.call('homeWidgetFrameSize', s),
    // The steps where the manifest's per-step span changes the width the body
    // spans ("5:m"; functions/homeWidgetFrame.js homeWidgetSpanFit).
    'data-span-fit': (el, s) => el.call('homeWidgetSpanFit', s),
    'data-widget-kind': (el, s) => (s && s.kind) || 'widget',
    'data-editing': (el, s) => (s.root._homeCustomizeMode ? 'true' : null),
    'data-dragging': (el, s) => (s.root._homeDragId === s.id ? 'true' : null),
    // This card's (?) panel is open (functions/homeWidgetHelpTip.js).
    'data-help-open': (el, s) => (el.call('homeWidgetHelpOpen', s) ? 'true' : null),
    // Under the assistant's generated board (functions/homeWidgetFrame.js
    // homeWidgetUnderView): inert, hidden from assistive tech, faded out.
    'data-under-view': (el, s) => (el.call('homeWidgetUnderView', s) ? 'true' : null),
    inert: (el, s) => (el.call('homeWidgetUnderView', s) ? '' : null),
    'aria-hidden': (el, s) => (el.call('homeWidgetUnderView', s) ? 'true' : null)
  },
  hide: (el, s) => el.call('homeWidgetFrameHidden', s),
  onRemove: (el) => {
    if (el.__homeClipTimer != null) clearTimeout(el.__homeClipTimer)
    el.__homeClipTimer = null
  },
  order: (el, s) => String(el.call('homeWidgetFrameOrder', s)),
  // The accent variables, plus the card's own per-step span variables
  // (`--home-span-c-5` …) the board's size rules read.
  style: (el, s) => el.call('homeWidgetFrameStyle', s, el.context && el.context.designSystem),

  // THE ACCENT SELECTOR (HOME-WIDGET-ACCENT-DARK-PLATE-INVISIBLE-1). The style
  // above publishes BOTH halves of the on-surface accent
  // (`--home-widget-accent-light` / `--home-widget-accent-dark`,
  // functions/homeWidgetFrame.js) because which one reads on the card depends
  // on the theme, not just on the hue — `analytics` measures 15.88:1 on the
  // light card and 1.19:1 on the dark one. These two lines are what collapses
  // them into the single `--home-widget-accent` every visual slot reads, and
  // they have to be a RULE, not an inline value: an inline style is computed
  // once at render and nothing re-renders a card on a `data-theme` flip, while
  // `[data-theme="dark"] .<card>` follows the flip for free.
  '--home-widget-accent': 'var(--home-widget-accent-light)',
  '@dark': { '--home-widget-accent': 'var(--home-widget-accent-dark)' },

  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  gap: 'Z',
  minWidth: '0',
  minHeight: '0',
  boxSizing: 'border-box',
  padding: 'A',
  fontSize: 'A',
  round: 'B1',
  background: 'var(--home-card-bg, var(--theme-card-background))',
  color: 'title',
  cursor: (el, s) => (s.root._homeCustomizeMode ? 'grab' : 'default'),

  // Enter: one fade-up per card, staggered by flow position — opacity and
  // transform only; the brand reset lands the final frame at once under
  // reduced motion. `backwards` holds the start frame through the delay, so a
  // card never flashes before its turn.
  animation: 'fadeInUpShort',
  animationDuration: 'C',
  animationTimingFunction: 'defaultBezier',
  animationFillMode: 'backwards',
  // The timing scale has no step below Z, so the 40 ms stagger is literal
  // (HOME_WIDGETS_SPEC §5).
  animationDelay: (el, s) =>
    Math.min(Number(el.call('homeWidgetFrameOrder', s)) || 0, 8) * 40 + 'ms',

  // The crossfade under the generated board (spec §5: `C`, opacity only): the
  // card keeps its cell — nothing on the board moves — and its visibility
  // drops after the fade, so it takes no pointer while hidden.
  transition:
    'opacity C defaultBezier, visibility 0s linear 0s, translate C defaultBezier, clip-path C defaultBezier, scale A defaultBezier',
  '&[data-under-view]': {
    opacity: '0',
    visibility: 'hidden',
    pointerEvents: 'none',
    transition: 'opacity C defaultBezier, visibility 0s linear C'
  },

  // Hover: an inset wash over the card — nothing moves, nothing scales.
  '::after': {
    content: '""',
    position: 'absolute',
    inset: '0',
    borderRadius: 'inherit',
    background: 'cardHover',
    opacity: '0',
    pointerEvents: 'none',
    transition: 'opacity A defaultBezier'
  },
  ':hover::after': { opacity: '1' },
  // The title button is the card's keyboard entry; the global ring draws on
  // the whole card while it has focus.
  ':has(.widget-title:focus-visible)': { shadow: 'focusRing' },
  '&[data-editing]': { touchAction: 'none', userSelect: 'none' },
  // The lifted card (DESIGN_SPEC §6.4): above its neighbours on the `float`
  // shadow — the elevation a dragged card takes (DESIGN_SPEC P1).
  '&[data-dragging]': { zIndex: '3', shadow: 'float' },
  // A card with its help panel open sits over its neighbours: a long help on a
  // short card runs past the card, and the next card's header (z 2, later in
  // the page) painted over it. Still far below every dialog.
  '&[data-help-open]': { zIndex: '3' },
  // The resize handle shows where the card is being looked at or worked on;
  // hidden, it takes no pointer, so a tap on the corner still opens the card.
  // It rises into place as it fades in (the edit controls' own entrance).
  ':hover .widget-resize-handle': { opacity: '1', pointerEvents: 'auto', translate: '0 0' },
  ':focus-within .widget-resize-handle': { opacity: '1', pointerEvents: 'auto', translate: '0 0' },
  '&[data-editing] .widget-resize-handle': {
    opacity: '1',
    pointerEvents: 'auto',
    translate: '0 0'
  },
  // Edit mode's controls (remove, size picker) are always mounted — entering
  // and leaving edit mode FADES them with a slight lift instead of popping
  // them in (Nika 2026-09-28 "smoother edit mode"); at rest they are `inert`
  // (no pointer, no focus, no reading) and they never move the card's content
  // (Rule 66: overlays).
  '&[data-editing] .widget-remove': { opacity: '1', pointerEvents: 'auto', translate: '0 0' },
  '&[data-editing] .widget-size-picker': { opacity: '1', pointerEvents: 'auto', translate: '0 0' },

  // A click on the card's chrome (the plate, the header, the padding) opens
  // the app. A click inside the BODY belongs to the body: its rows open their
  // own records, so the card never answers it too (one click, one action —
  // the contract both planes had before the frame).
  onClick: (e, el, s) => {
    if (s && s.root && s.root._homeCustomizeMode) return
    const t = e.target
    if (
      t &&
      t.closest &&
      t.closest('button, a, input, textarea, select, [contenteditable], .gs-no-drag')
    ) {
      return
    }
    const body = el.Body && el.Body.node
    if (body && t && body.contains && body.contains(t)) return
    el.call('homeWidgetOpen', s, e)
  },
  // A pointer resting on the card — or focus reaching it — starts the chunk
  // load of the app it opens, so the click finds it warm and the window fills
  // in at once (functions/slotPrefetch.js; nothing mounts, nothing is written).
  onPointerenter: (e, el, s) => el.call('prefetchSlotRoute', el.call('homeWidgetOpenRoute', s)),
  onFocusin: (e, el, s) => el.call('prefetchSlotRoute', el.call('homeWidgetOpenRoute', s)),
  onPointerdown: (e, el, s) => el.call('homeWidgetDragStart', e, s),
  onPointermove: (e, el, s) => el.call('homeWidgetDragMove', e, s),
  onPointerup: (e, el, s) => el.call('homeWidgetDragEnd', e, s, false),
  onPointercancel: (e, el, s) => el.call('homeWidgetDragEnd', e, s, true),
  onContextmenu: (e, el, s) => {
    if (!el.call('homeContextMenuAllowed', e, s.root)) return
    e.preventDefault()
    e.stopPropagation()
    const title = el.call('polyglot', (s && s.title) || 'workspace.menu.thisWidget')
    const aiRef = { id: s.id, title }
    const canEditAi = !!s.aiGenerated && !!s.viewerCanEdit
    const canDeleteAi = !!s.aiGenerated && !!s.viewerCanDelete
    const items = [
      { label: 'common.action.refresh', action: 'refresh' },
      {
        label:
          s.root && s.root._homeWidgetColors && s.root._homeWidgetColors[s.id]
            ? 'workspace.menu.changeColor'
            : 'workspace.menu.addColor',
        action: 'widgetColor',
        widgetId: s.id,
        x: e.clientX,
        y: e.clientY,
        appHue: s.hue || s.iconBg || (s.spec && s.spec.iconBg) || null
      },
      {
        label: el.call('homeWidgetHideLabel', s),
        action: 'hideWidget',
        widgetId: s.id
      }
    ]
    // "Change with AI…" — an app's widget, a pinned one, or an AI-made widget
    // this member may edit (functions/homeContextMenu.js homeWidgetAiTarget);
    // second, after Refresh. HOME-WIDGET-CHANGE-WITH-AI-1.
    const aiTarget = el.call('homeWidgetAiTarget', s, title)
    if (aiTarget) {
      items.splice(1, 0, {
        label: 'workspace.menu.changeWithAi',
        action: 'changeWidgetWithAi',
        widget: aiTarget
      })
    }
    // "Widget size" — a touch screen's way to the size (a long press opens
    // this menu): the edit-mode picker's rows, without edit mode. Before the
    // hide row; absent for a mouse (functions/homeWidgetFrame.js
    // homeWidgetSizeMenuRow).
    const sizeRow = el.call('homeWidgetSizeMenuRow', s)
    if (sizeRow) {
      const at = items.findIndex((it) => it.action === 'hideWidget')
      items.splice(at === -1 ? items.length : at, 0, sizeRow)
    }
    if (canEditAi) {
      items.push({
        label:
          s.visibility === 'workspace'
            ? 'workspace.menu.makePersonal'
            : 'workspace.menu.shareToWorkspace',
        action: s.visibility === 'workspace' ? 'unshareAiWidget' : 'shareAiWidget',
        widget: aiRef
      })
    }
    items.push({ label: 'workspace.menu.customize', action: 'customize' })
    // The destructive row takes the menu's `danger` flag (ContextDropdown row
    // states: `dangerInk` + `data-danger`), never a raw hue, and it is the
    // last item (CRUD confirm standard rule 7), after Customize.
    if (canDeleteAi) {
      items.push({
        label: el.call('polyglot', 'workspace.menu.deleteWidget').replace('{title}', title),
        action: 'deleteAiWidget',
        widget: aiRef,
        danger: true
      })
    }
    s.root.update(
      {
        contextDropdown: {
          open: true,
          x: e.clientX,
          y: e.clientY,
          items,
          actionFn: 'homeContextAction'
        }
      },
      { preventFetch: true }
    )
    el.call('focusHomeContextMenu')
  },

  Header: {
    class: 'widget-header',
    cursor: (el, s) =>
      !s.root._homeCustomizeMode && el.call('homeWidgetOpenRoute', s) ? 'pointer' : 'inherit',
    display: 'flex',
    alignItems: 'flex-start',
    gap: 'Z',
    minWidth: '0',
    flexShrink: '0',
    // The (?) panel's containing block (measured 2026-09-10): anchored to the
    // glyph, the panel tracked the title's length and hung 64px outside the
    // card, and its `100%` cap measured a 17px button. The header gives it the
    // card's width; the bump paints it over the body and the next card.
    position: 'relative',
    zIndex: '2',

    HomeWidgetPlate: {
      Icon: { name: (el, s) => (s && s.icon) || 'star' }
    },

    TitleRow: {
      display: 'flex',
      alignItems: 'center',
      gap: 'X',
      flex: '1',
      minWidth: '0',
      minHeight: 'iconXl',

      Title: {
        tag: 'button',
        type: 'button',
        class: 'widget-title',
        minWidth: '0',
        padding: '0',
        border: 'none',
        background: 'transparent',
        color: 'title',
        fontSize: 'Z',
        fontWeight: '500',
        // 1.5, the house body leading: Georgian glyphs carry descenders 0.31em
        // under the baseline, and a tighter box crowds a two-line title.
        lineHeight: '1.5',
        textAlign: 'left',
        overflowWrap: 'anywhere',
        cursor: 'inherit',
        // A finger's target: the title is 19-22px tall (dev DFI, touch
        // simulated, DESIGN-SHELL-RESPONSIVE-IOS-SAFARI-1) — the 44pt floor
        // (the shared kit's `touchFloor`, shared/components/_touchTarget.js),
        // stated inline: this frame is closure-free and imports nothing
        // (widgetHelp.test.js).
        '@coarse': {
          position: 'relative',
          ':after': {
            content: '""',
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: '100%',
            height: '100%',
            minWidth: 'controlH',
            minHeight: 'controlH',
            transform: 'translate(-50%, -50%)'
          }
        },
        text: (el, s) => el.call('polyglot', (s && (s.title || s.id)) || ''),
        onClick: (e, el, s) => {
          e.stopPropagation()
          if (s && s.root && s.root._homeCustomizeMode) return
          el.call('homeWidgetOpen', s, e)
        },
        // Edit mode: the arrow keys move the widget in the order — the
        // keyboard path of drag-to-reorder.
        onKeydown: (e, el, s) => el.call('homeWidgetKeyMove', e, s)
      },

      // The OPTIONAL (?) beside the name (functions/widgetHelp.js contract).
      QuestionMarkTooltip: {
        class: 'gs-no-drag widget-header-help',
        hide: (el, s) => !el.call('homeWidgetHelpOf', s),
        // Not the panel's containing block — the header is (see above).
        position: 'static',
        margin: '0',
        flexShrink: '0',
        lineHeight: '1',
        height: 'fit-content',
        // The panel's place is measured on the pointer's approach (before the
        // brand's hover/tap open), never in render; any other open (keyboard,
        // code) measures once at that open (functions/homeWidgetHelpTip.js).
        onPointerenter: (e, el) => el.call('homeWidgetHelpTipMeasure'),
        // …and goes stale when the pointer or focus leaves (never shown open
        // again; the next approach measures afresh).
        onPointerleave: (e, el) => el.call('homeWidgetHelpTipForget'),
        onFocusout: (e, el) => el.call('homeWidgetHelpTipForget'),
        tip: (el, s) => {
          const h = el.call('homeWidgetHelpOf', s)
          return h && h.tip ? el.call('polyglot', h.tip) : ''
        },
        ariaLabel: (el, s) => {
          const h = el.call('homeWidgetHelpOf', s)
          return h && h.ariaLabel ? el.call('polyglot', h.ariaLabel) : ''
        },
        Icon: { boxSize: 'A', color: 'currentColor', opacity: '.5' },
        TooltipHidden: {
          shapeDirection: 'top',
          maxWidth: 'min(var(--spacing-G1), 100%, 92vw)',
          // PLACED AT ITS (?) (WS-WIDGET-HELP-TOOLTIP-ANCHOR-1): the header
          // above stays the clamp; the panel opens from the glyph, shifted
          // inward only as far as the card and the app frame need, and flips
          // above the (?) when there is no room below
          // (functions/homeWidgetHelpTip.js). Replaces the brand's centring.
          style: (el) => el.call('homeWidgetHelpTipStyle'),
          // It renders from the place stored on THIS card's state, so it re-runs
          // when that lands (no root write: other cards never re-run).
          styleDeps: [(el, s) => s.root.helpTooltipOpenId, (el, s) => s._helpTipPlace],
          // Its place was measured for this window size — a resize closes it.
          onWindowResize: (e, el) => el.call('homeWidgetHelpTipClose'),
          description: (el, s) => {
            const h = el.call('homeWidgetHelpOf', s)
            return h && h.tip ? el.call('polyglot', h.tip) : ''
          }
        }
      }
    },

    HomeWidgetAction: {
      hide: (el, s) => !el.call('homeWidgetActionOf', s),
      title: (el, s) => {
        const a = el.call('homeWidgetActionOf', s)
        if (!a) return null
        const name = el.call('polyglot', (s && (s.title || s.id)) || '')
        return el.call('polyglot', a.label).replace('{app}', name)
      },
      'aria-label': (el, s) => {
        const a = el.call('homeWidgetActionOf', s)
        if (!a) return null
        const name = el.call('polyglot', (s && (s.title || s.id)) || '')
        return el.call('polyglot', a.label).replace('{app}', name)
      },
      Icon: {
        name: (el, s) => {
          const a = el.call('homeWidgetActionOf', s)
          return (a && a.icon) || 'arrowUpRight'
        }
      },
      onClick: (e, el, s) => {
        e.stopPropagation()
        e.preventDefault()
        if (s && s.root && s.root._homeCustomizeMode) return
        const card = el.parent && el.parent.parent
        const body = card && card.Body
        el.call('homeWidgetRunAction', s, e, body && body.content ? body.content : body)
      }
    }
  },

  Body: {
    class: 'widget-body',
    flex: '1',
    minHeight: '0',
    minWidth: '0',
    display: 'flex',
    flexDirection: 'column',
    // above the card's hover wash, under the header's bump (2): a hover
    // darkens the card's fill, never its words. The card itself makes no
    // stacking context (the resize outline paints over the neighbours).
    position: 'relative',
    zIndex: '1'
  },

  // Edit mode — overlays, so entering edit mode moves nothing (Rule 66).
  CloseButton: {
    class: 'gs-no-drag widget-remove',
    // Shown by the card's `[data-editing]` rule above (a fade with a lift);
    // at rest: invisible and inert — no pointer, no focus, no reading.
    opacity: '0',
    pointerEvents: 'none',
    translate: '0 var(--spacing-X)',
    transition: 'opacity A defaultBezier, translate A defaultBezier',
    attr: { inert: (el, s) => (s.root._homeCustomizeMode ? null : '') },
    position: 'absolute',
    top: 'X',
    left: 'X',
    zIndex: '4',
    theme: 'layer',
    // Named for ITS widget — "Hide “{title}”" (homeWidgetHideLabel), never one
    // shared "Hide this widget" for every card.
    title: (el, s) => el.call('homeWidgetHideLabel', s),
    'aria-label': (el, s) => el.call('homeWidgetHideLabel', s),
    onPointerdown: (e) => e.stopPropagation(),
    onClick: (e, el, s) => {
      e.stopPropagation()
      if (s && s.root && s.root._homeOps && s.root._homeOps.hideWidget)
        s.root._homeOps.hideWidget(s.id)
    }
  },

  AppSegmented: {
    class: 'gs-no-drag widget-size-picker',
    compact: true,
    // Shown by the card's `[data-editing]` rule above, like the remove button.
    opacity: '0',
    pointerEvents: 'none',
    translate: '0 var(--spacing-X)',
    transition: 'opacity A defaultBezier, translate A defaultBezier',
    attr: { inert: (el, s) => (s.root._homeCustomizeMode ? null : '') },
    position: 'absolute',
    bottom: 'Z',
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: '4',
    ariaLabel: (el) => el.call('polyglot', 'workspace.widget.size.label'),
    current: (el, s) => el.call('homeWidgetFrameSize', s),
    select: 'homeWidgetSetSize',
    onPointerdown: (e) => e.stopPropagation(),
    childrenAs: 'state',
    children: (el, s) => el.call('homeWidgetSizeRows', s)
  },

  // The box a resize will give — the dashed outline from the card's top-left
  // corner, painted over the neighbours (the card makes no stacking context,
  // so the outline's z-index lifts it above them). Its own state `{ size, w,
  // h }` is written by functions/homeWidgetResize.js while the pointer moves;
  // `size: null` = no resize in flight. The box glides between sizes at `A`.
  WidgetResizeGhost: {
    class: 'widget-resize-ghost',
    state: { size: null, w: 0, h: 0 },
    hide: (el, s) => !s.size,
    ariaHidden: 'true',
    position: 'absolute',
    top: '0',
    left: '0',
    zIndex: '5',
    boxSizing: 'border-box',
    pointerEvents: 'none',
    round: 'B1',
    borderWidth: 'hairline2',
    borderStyle: 'dashed',
    borderColor: 'caption',
    background: 'hover',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'width A defaultBezier, height A defaultBezier',
    style: (el, s) => ({ width: `${Number(s.w) || 0}px`, height: `${Number(s.h) || 0}px` }),
    WidgetResizeGhostSize: {
      tag: 'span',
      theme: 'layer',
      padding: 'X Z',
      round: 'radiusControl',
      fontSize: 'Z',
      fontWeight: '500',
      color: 'title',
      // the size codes are symbols, the same in every language (the picker's)
      text: (el, s) => ({ s: 'S', m: 'M', l: 'L', xl: 'XL' })[s.size] || ''
    }
  },

  // The corner handle (components/HomeWidgetResizeHandle.js): drag to resize,
  // arrows step the size while it has focus, Escape cancels a drag.
  HomeWidgetResizeHandle: {}
}
